import * as THREE from 'three';
import { basic, emis, std, stone } from '../materials.ts';
import { M, V, glowSprite } from '../prims.ts';
import { kit } from './era4-kit.ts';
import { prismAt } from './prism.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * The Chasm's props that are struck like nightmares (docs/SIMA.md «Guardianes»; NIGHTMARES `crystal`, `cocoon`,
 * `iceBlock`). Each stands still and has the clips the battle view asks for: 'idle' (and 'walk', 'attack' and
 * 'stun' the same), 'hit' (a shudder) and 'die' (it breaks apart).
 *
 * - crystal: the prism's charge. A tall cluster of violet prisms on a split stone, its heart lit; a hero and a half
 *   tall. Bones: shardA, shardB, shardC (three clumps that fall apart in 'die').
 * - cocoon: the brood queen's egg. A cocoon of white silk standing on the floor, tied down by threads, the brood
 *   moving inside it ('idle' pulses). Bones: shellL, shellR (its two halves: 'die' splits them), brood (the glow
 *   inside).
 * - iceBlock: the frost lady's ice round a hero, a little taller and wider than one; clear, so the hero inside
 *   still reads, its edges frosted white. Bones: iceFL, iceFR, iceBL, iceBR (its four quarters: 'die' bursts them
 *   apart). Drawn at the hero's spot, its origin at the hero's feet.
 */

export const CRYSTAL_HEIGHT = 1.9;
export const COCOON_HEIGHT = 1.0;
export const ICE_BLOCK_HEIGHT = 2.25;

export const crystal = (o?: BaseOpts): Model => {
  const c = begin(o, 0.6, 2909);
  const { g, rng } = c;
  const k4 = kit(rng);
  const glass = emis('#dcb6ff', '#9b3fe0', 0.75, { transparent: true, opacity: 0.86, roughness: 0.1, flatShading: true });
  const deep = std('#5a2d7e', { roughness: 0.18, metalness: 0.3, flatShading: true });
  const rock = stone('#26232c', { bumpScale: 0.05 });
  const heart = emis('#fbefff', '#c56cff', 2.4);

  // The split stone it grows from.
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2 + 0.4;
    k4.lump(g, rock, Math.cos(a) * 0.32, 0.1, Math.sin(a) * 0.3, 0.22, 0.13, 0.2, 0.5, 0);
  }
  M(new THREE.OctahedronGeometry(0.12, 0), heart, g).position.set(0, 0.32, 0.05);
  glowSprite(g, '#c56cff', 1.6, V(0, 0.6, 0), 0.4);
  glowSprite(g, '#c56cff', 1.2, V(0, 0.1, 0), 0.25);

  const clumps: THREE.Object3D[] = [];
  const DEF: [number, number, number, [number, number, number][]][] = [
    // [bone x, z, lean, prisms: [dx, dz, len]]
    [0.0, 0.0, 0, [[0, 0, 1.65], [0.12, 0.08, 1.15], [-0.1, 0.1, 0.95]]],
    [0.22, -0.08, 0.5, [[0, 0, 1.0], [0.1, -0.1, 0.7], [0.05, 0.12, 0.55]]],
    [-0.22, -0.05, -0.5, [[0, 0, 1.1], [-0.12, 0.05, 0.75], [-0.04, -0.14, 0.5]]],
  ];
  DEF.forEach(([x, z, lean, prisms], i) => {
    const cb = joint(g, V(x, 0.12, z), `shard${'ABC'[i]}`);
    for (const [dx, dz, len] of prisms) {
      const dir = V(lean * 0.6 + dx * 1.5 + (rng() - 0.5) * 0.15, 1, dz * 1.5 + (rng() - 0.5) * 0.15);
      prismAt(cb.abs, len > 1.2 || (i + len * 10) % 3 > 1 ? glass : deep, V(x + dx * 0.5, 0.1, z + dz * 0.5), dir, 0.08 + len * 0.05, len, rng);
    }
    clumps.push(cb.p);
  });
  return done(c, CRYSTAL_HEIGHT, () => undefined, undefined, bonesOf(c, { shardA: clumps[0]!, shardB: clumps[1]!, shardC: clumps[2]! }));
};

export const cocoon = (o?: BaseOpts): Model => {
  const c = begin(o, 0.5, 3119);
  const { g, rng } = c;
  const k4 = kit(rng);
  const silk = std('#d3dbe8', { roughness: 0.9, bumpScale: 0.06 });
  const silkD = std('#a9a3bd', { roughness: 0.9 });
  const glow = emis('#e6dcff', '#a98cff', 1.2);
  const thread = basic('#eef0f6', 0.45);

  // Threads tying it to the floor.
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + 0.3;
    const top = V(Math.cos(a) * 0.2, 0.45, Math.sin(a) * 0.2);
    const end = V(Math.cos(a) * 0.62, 0.01, Math.sin(a) * 0.62);
    k4.tube(g, [top, top.clone().lerp(end, 0.5).add(V(0, -0.08, 0)), end], 0.008, thread, 3);
  }
  const mat = M(new THREE.CircleGeometry(0.5, 12), thread, g);
  mat.rotation.x = -Math.PI / 2;
  mat.position.y = 0.01;

  // The brood inside: dark shapes in a violet glow, seen where the silk is thin.
  const broodB = joint(g, V(0, 0.48, 0), 'brood');
  M(new THREE.SphereGeometry(0.16, 8, 6), glow, broodB.abs).position.set(0, 0.48, 0);
  glowSprite(broodB.abs, '#b59cff', 0.9, V(0, 0.5, 0.05), 0.45);

  // The two halves of the cocoon (open toward each other, so they can split).
  const shells: THREE.Object3D[] = [];
  for (const k of [-1, 1]) {
    const sB = joint(g, V(0, 0.08, 0), k < 0 ? 'shellL' : 'shellR');
    // (A sphere's half from phi 0 is its front; turned a quarter it is the right half, from phi π the left.)
    const half = M(new THREE.SphereGeometry(1, 12, 10, k < 0 ? Math.PI : 0, Math.PI), silk, sB.abs);
    half.position.set(0, 0.5, 0);
    half.scale.set(0.3, 0.46, 0.3);
    half.rotation.set(0, Math.PI / 2, k * 0.04);
    // Strands wound round it, and a seam where the brood pushes.
    for (let i = 0; i < 3; i++) {
      const y = 0.3 + i * 0.17 + (rng() - 0.5) * 0.05;
      const r = 0.3 * Math.sqrt(Math.max(0, 1 - ((y - 0.5) / 0.46) ** 2)) + 0.01;
      const band = M(new THREE.TorusGeometry(r, 0.014, 3, 10, Math.PI), i % 2 ? silkD : silk, sB.abs);
      band.position.set(0, y, 0);
      band.rotation.set(Math.PI / 2 + (i - 1) * 0.3 * k, 0, k > 0 ? -Math.PI / 2 : Math.PI / 2);
    }
    shells.push(sB.p);
  }
  // The twist of silk at its top rides the left half.
  const shellAbs = (shells[0] as THREE.Group).children[0] as THREE.Group;
  const top = M(new THREE.ConeGeometry(0.08, 0.2, 6), silkD, shellAbs);
  top.position.set(0, 0.98, 0);
  return done(c, COCOON_HEIGHT, () => undefined, undefined, bonesOf(c, { shellL: shells[0]!, shellR: shells[1]!, brood: broodB.p }));
};

export const iceBlock = (o?: BaseOpts): Model => {
  const c = begin(o, 0.75, 6007);
  const { g, rng } = c;
  const k4 = kit(rng);
  const clear = std('#bfe0ff', { transparent: true, opacity: 0.32, flatShading: true, roughness: 0.05, metalness: 0.1, depthWrite: false, emissive: '#3d6a90', emissiveIntensity: 0.25, bumpScale: 0 });
  const frost = std('#d2e6f4', { roughness: 0.85, flatShading: true });
  const cold = emis('#e9f7ff', '#8fd0ff', 1.6);

  const W = 0.62, D = 0.5, H = ICE_BLOCK_HEIGHT;
  const quarters: THREE.Object3D[] = [];
  for (const [kx, kz, name] of [[-1, 1, 'iceFL'], [1, 1, 'iceFR'], [-1, -1, 'iceBL'], [1, -1, 'iceBR']] as const) {
    const qB = joint(g, V(kx * W * 0.5, 0, kz * D * 0.5), name);
    const q = qB.abs;
    // A quarter of the block: a craggy lump of clear ice, shards breaking out of its top.
    const cx = kx * W * 0.42, cz = kz * D * 0.42;
    k4.lump(q, clear, cx, H * 0.5, cz, W * 0.66, H * 0.53, D * 0.7, 0.12, 1, 0.14);
    k4.shard(q, clear, V(cx * 1.2, H * 0.85, cz * 1.2), V(kx * 0.4, 1, kz * 0.3), 0.55 + rng() * 0.3, 0.12);
    k4.shard(q, frost, V(cx * 1.6, H * 0.7, cz * 1.5), V(kx, 0.7, kz * 0.6), 0.3 + rng() * 0.15, 0.05);
    // Frost crusting its foot and its shoulder.
    k4.lump(q, frost, kx * W * 0.75, 0.08, kz * D * 0.75, 0.3, 0.1, 0.26, 0.4, 0);
    k4.lump(q, frost, cx * 1.3, H * 0.93, cz * 1.2, 0.18, 0.06, 0.16, 0.4, 0);
    // A crack of cold light inside it.
    k4.crack(q, [V(kx * W * 0.85, H * (0.2 + rng() * 0.3), kz * (D * 0.9)), V(kx * W * 0.55, H * (0.45 + rng() * 0.1), kz * (D * 1.05)), V(kx * W * 0.25, H * (0.62 + rng() * 0.2), kz * (D * 1.1))], 0.008, cold, 0.04);
    quarters.push(qB.p);
  }
  glowSprite(g, '#bfe3ff', 1.6, V(0, H * 0.5, 0), 0.18);
  glowSprite(g, '#cfe8ff', 1.2, V(0, 0.1, 0), 0.25);
  return done(c, ICE_BLOCK_HEIGHT, () => undefined, undefined, bonesOf(c, { iceFL: quarters[0]!, iceFR: quarters[1]!, iceBL: quarters[2]!, iceBR: quarters[3]! }));
};
